import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSite } from "@/contexts/SiteContent";
import RichText from "@/components/RichText";

function fmt(n) { return Math.round(n).toLocaleString("en-IN"); }

const AMOUNT_MIN = 1000, AMOUNT_MAX = 50000000, AMOUNT_STEP = 1000;
const RATE_MIN = 0, RATE_MAX = 30, RATE_STEP = 0.1;
const TENURE_MIN = 1, TENURE_MAX = 360;

export default function EMICalculatorPage() {
  const { content } = useSite();
  const ep = content?.emi_page?.hero || {};
  const [mode, setMode] = useState("irr"); // irr | flat
  const [amount, setAmount] = useState(1000000);
  const [rate, setRate] = useState(10.5);
  const [tenure, setTenure] = useState(60);

  // text-input proxies for fluid typing
  const [amountText, setAmountText] = useState(String(amount));
  const [rateText, setRateText] = useState(String(rate));
  const [tenureText, setTenureText] = useState(String(tenure));

  useEffect(() => { setAmountText(String(amount)); }, [amount]);
  useEffect(() => { setRateText(String(rate)); }, [rate]);
  useEffect(() => { setTenureText(String(tenure)); }, [tenure]);

  const { emi, totalInterest, totalPayment, schedule } = useMemo(() => {
    const n = tenure;
    let emiV, totalI, totalP;
    const sched = [];
    if (mode === "flat") {
      totalI = amount * (rate / 100) * (n / 12);
      totalP = amount + totalI;
      emiV = totalP / n;
      // For flat: linear principal repayment for chart purposes
      const principalPerMonth = amount / n;
      let bal = amount;
      for (let m = 1; m <= n; m++) {
        bal -= principalPerMonth;
        if (m % 12 === 0 || m === n) sched.push({ year: Math.ceil(m / 12), balance: Math.max(0, bal) });
      }
    } else {
      const r = rate / 12 / 100;
      if (r === 0) {
        emiV = amount / n;
        totalI = 0;
        totalP = amount;
      } else {
        emiV = (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        totalP = emiV * n;
        totalI = totalP - amount;
      }
      let bal = amount;
      for (let m = 1; m <= n; m++) {
        const interest = bal * r;
        const principal = emiV - interest;
        bal -= principal;
        if (m % 12 === 0 || m === n) sched.push({ year: Math.ceil(m / 12), balance: Math.max(0, bal) });
      }
    }
    return { emi: emiV, totalInterest: totalI, totalPayment: totalP, schedule: sched };
  }, [amount, rate, tenure, mode]);

  const chartData = [
    { name: "Principal", value: amount },
    { name: "Interest", value: Math.max(0, totalInterest) },
  ];
  const COLORS = ["#020617", "#E67E22"];

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-20">
      <motion.div initial={{opacity:0, y:14}} animate={{opacity:1, y:0}} transition={{duration:0.5}}>
        <div className="overline">{ep.eyebrow || "EMI Calculator"}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-3 max-w-3xl">{ep.title || "Plan your EMI before you apply."}</h1>
        <RichText html={ep.subtitle} variant="subtitle" className="mt-6 max-w-3xl"/>
      </motion.div>

      <div className="mt-12 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 card-flat p-7 sm:p-9 space-y-8">
          {/* Mode toggle */}
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Interest method</Label>
            <div className="mt-2 inline-flex p-1 rounded-full bg-slate-100 relative" data-testid="emi-mode-toggle">
              <motion.div
                className="absolute top-1 bottom-1 w-1/2 rounded-full bg-slate-950"
                animate={{ x: mode === "irr" ? 0 : "100%" }}
                transition={{ type: "spring", stiffness: 300, damping: 28 }}
                style={{ left: "4px", right: "4px", width: "calc(50% - 4px)" }}
              />
              {["irr", "flat"].map(m => (
                <button key={m} data-testid={`emi-mode-${m}`} onClick={() => setMode(m)}
                  className={`relative z-10 px-6 py-2 text-sm font-semibold rounded-full transition-colors ${mode === m ? "text-white" : "text-ink-muted"}`}>
                  {m === "irr" ? "IRR (Reducing)" : "Flat Rate"}
                </button>
              ))}
            </div>
            <div className="text-xs text-ink-muted mt-2">When you pick one method the other locks — switching recomputes EMI with current values.</div>
          </div>

          <ControlRow
            label="Loan amount" prefix="₹"
            value={amount} setValue={(v)=>setAmount(clamp(Math.round(v / AMOUNT_STEP) * AMOUNT_STEP, AMOUNT_MIN, AMOUNT_MAX))}
            text={amountText} setText={setAmountText}
            commit={()=>setAmount(clamp(Math.round((Number(amountText)||AMOUNT_MIN) / AMOUNT_STEP) * AMOUNT_STEP, AMOUNT_MIN, AMOUNT_MAX))}
            min={AMOUNT_MIN} max={AMOUNT_MAX} step={AMOUNT_STEP}
            testid="emi-amount"
          />
          <ControlRow
            label="Interest rate" suffix="% p.a."
            value={rate} setValue={(v)=>setRate(clamp(Math.round(v * 10) / 10, RATE_MIN, RATE_MAX))}
            text={rateText} setText={setRateText}
            commit={()=>setRate(clamp(Math.round((Number(rateText)||0) * 10) / 10, RATE_MIN, RATE_MAX))}
            min={RATE_MIN} max={RATE_MAX} step={RATE_STEP}
            testid="emi-rate"
          />
          <ControlRow
            label="Tenure" suffix={`months  (${(tenure/12).toFixed(1)} yrs)`}
            value={tenure} setValue={(v)=>setTenure(clamp(Math.round(v), TENURE_MIN, TENURE_MAX))}
            text={tenureText} setText={setTenureText}
            commit={()=>setTenure(clamp(Math.round(Number(tenureText)||TENURE_MIN), TENURE_MIN, TENURE_MAX))}
            min={TENURE_MIN} max={TENURE_MAX} step={1}
            testid="emi-tenure"
          />

          <div className="grid sm:grid-cols-3 gap-4 pt-2">
            <Stat label="Monthly EMI" value={`₹${fmt(emi)}`} primary testid="emi-result-emi"/>
            <Stat label="Total interest" value={`₹${fmt(totalInterest)}`} testid="emi-result-interest"/>
            <Stat label="Total payment" value={`₹${fmt(totalPayment)}`} testid="emi-result-total"/>
          </div>
          <Button data-testid="emi-apply-cta" onClick={() => window.dispatchEvent(new CustomEvent("tara:open-wizard"))} className="btn-saffron rounded-full h-12 px-7">Apply with these terms</Button>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <motion.div initial={{opacity:0, y:14}} whileInView={{opacity:1, y:0}} viewport={{once:true}} className="card-flat p-6">
            <div className="overline">Breakdown</div>
            <div className="h-56 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={56} outerRadius={86} paddingAngle={2}>
                    {chartData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `₹${fmt(v)}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-slate-950"/>Principal</div>
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-sm bg-saffron"/>Interest</div>
            </div>
          </motion.div>

          <div className="card-flat p-6">
            <div className="overline">Yearly closing balance</div>
            <div className="mt-3 max-h-56 overflow-y-auto" data-testid="emi-schedule-table">
              <table className="w-full text-sm">
                <thead className="text-left text-ink-muted">
                  <tr className="border-b border-slate-200"><th className="py-2">Year</th><th className="py-2 text-right">Outstanding</th></tr>
                </thead>
                <tbody>
                  {schedule.map(r => (
                    <tr key={r.year} className="border-b border-slate-100 last:border-0">
                      <td className="py-2">{r.year}</td>
                      <td className="py-2 text-right font-medium">₹{fmt(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ControlRow({ label, prefix, suffix, value, setValue, text, setText, commit, min, max, step, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <Label className="text-xs uppercase tracking-[0.18em] text-ink-muted">{label}</Label>
        <div className="flex items-center gap-2">
          {prefix && <span className="text-sm text-ink-muted">{prefix}</span>}
          <Input
            data-testid={`${testid}-input`}
            type="text" inputMode="decimal"
            value={text}
            onChange={(e) => setText(e.target.value.replace(/[^\d.]/g, ""))}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
            className="h-9 w-36 text-right font-display font-bold"
          />
          {suffix && <span className="text-sm text-ink-muted">{suffix}</span>}
        </div>
      </div>
      <div className="mt-3">
        <Slider data-testid={`${testid}-slider`} value={[Number(value) || 0]} min={min} max={max} step={step} onValueChange={(v)=>setValue(v[0])}/>
      </div>
      <div className="flex justify-between text-[10px] uppercase tracking-wider text-ink-muted mt-2">
        <span>{prefix ? `${prefix}${fmt(min)}` : (label.includes("rate") ? `${min}%` : min)}</span>
        <span>{prefix ? `${prefix}${fmt(max)}` : (label.includes("rate") ? `${max}%` : max)}</span>
      </div>
    </div>
  );
}

function Stat({ label, value, primary, testid }) {
  return (
    <motion.div initial={{opacity:0, scale:0.96}} whileInView={{opacity:1, scale:1}} viewport={{once:true}}
      className={`rounded-2xl p-5 ${primary ? "bg-slate-950 text-white" : "bg-slate-50 border border-slate-200"}`} data-testid={testid}>
      <div className={`text-xs uppercase tracking-[0.18em] ${primary ? "text-saffron" : "text-ink-muted"}`}>{label}</div>
      <div className="font-display font-extrabold text-2xl sm:text-3xl mt-1">{value}</div>
    </motion.div>
  );
}
